// Copyright (c) MobiFactory. All rights reserved.
// This source code is the property of MobiFactory. It is exclusively available to customers who have a valid user license.
// See LICENSE.txt file in the project root for full license information.

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import oidcManager from "../authentication/oidcManager";
import { ElNotification } from "element-plus";
import { translate } from "../plugins/i18n";

/**
 * Duration of the notification popup
 * @type {number}
 */
const NOTIFICATION_DURATION = 2500;

/**
 * Wraps a result from the remote server
 */
export class HttpResult<T> implements AxiosResponse<T> {
  isSuccess = false;
  error: string | null = null;
  data!: T;
  status = 0;
  statusText = "";
  headers: any;
  config: AxiosRequestConfig = {};
  request: any;

  /**
   * Class constructor
   * @param {AxiosResponse} response - Backend response data
   * @param {object} error - Backend error
   */
  constructor(response: AxiosResponse | null, error?: string) {
    if (error) {
      this.isSuccess = false;
      this.error = error;
    } else {
      this.isSuccess = true;
    }
    if (response) {
      this.data = response.data;
      this.status = response.status;
      this.statusText = response.statusText;
      this.headers = response.headers;
      this.config = response.config;
      this.request = response.request;
    }
  }
}

export interface HttpRequestConfig extends AxiosRequestConfig {
  showNotificationOnSuccess?: boolean;
  showNotificationOnError?: boolean;
}

/**
 * Helper function for http connections to the backend
 * @param {string} baseUrl - Server api url
 * @returns {object} Axios instance
 */
const request = function (baseUrl: string) {
  const instance = axios.create({
    baseURL: baseUrl,
    headers: {
      Authorization: `Bearer ${oidcManager.accessToken}`,
      "Accept-Language": navigator.languages.join(","),
    },
  });
  instance.interceptors.response.use(
    // Always return the data property on success
    (response: AxiosResponse) => {
      return new HttpResult(response);
    },
    // Handle response error on rejected
    (error) => {
      if (error.config.showNotificationOnError === false) {
        return new HttpResult(null, error);
      }

      if (error.response) {
        handleError(error);
      } else {
        handleConnectionError();
      }
      return new HttpResult(error.response, error);
    }
  );
  return instance;
};

/**
 * Handles HTTP default Error
 * @param {object} error - Axios error
 */
const handleError = function (error: any) {
  ElNotification({
    title: translate("Error.ServerExceptionTitle"),
    message: error.response.data,
    duration: NOTIFICATION_DURATION,
    type: "error",
  });
};

/**
 * Handles Connection Error
 */
const handleConnectionError = function () {
  ElNotification({
    title: translate("Error.ServerConnectionTitle"),
    message: translate("Error.ServerConnectionContent"),
    duration: NOTIFICATION_DURATION,
    type: "error",
  });
};

/**
 * REST Http client
 */
export default class Http {
  baseUrlResolver: () => string;

  /**
   * Class constructor
   * @param {Function} baseUrlResolver - called to resolved base url
   */
  constructor(baseUrlResolver: () => string) {
    this.baseUrlResolver = baseUrlResolver;
  }

  /**
   * Sends a GET request to the backend
   * @param {string} url - Api url
   * @param {number} timeout - Specifies the number of milliseconds before the request times out.
   * @param {boolean} showNotificationOnError - Value indicating whether to show notifications on error
   * @returns {Promise} Response data
   */
  get(url: string, timeout = 0, showNotificationOnError = true): Promise<HttpResult<any>> {
    return request(this.baseUrlResolver()).get(url, {
      timeout: timeout,
      showNotificationOnError: showNotificationOnError,
    } as HttpRequestConfig);
  }

  /**
   * Sends a POST request to the backend
   * @param {string} url - Api url
   * @param {object} data - data to be send
   * @param {object} config - Axios configuration
   * @returns {Promise} Response data
   */
  post(url: string, data: any, config: HttpRequestConfig | null = null): Promise<HttpResult<any>> {
    return request(this.baseUrlResolver()).post(url, data, {
      showNotificationOnSuccess: false,
      showNotificationOnError: true,
      ...config,
    } as HttpRequestConfig);
  }

  /**
   * Sends a POST request to the backend with a blob reponseType
   * @param {string} url - Api url
   * @param {object} data - data to be send
   * @param {object} config - Axios configuration
   * @returns {Promise} Response data
   */
  postBlob(url: string, data: any, config: HttpRequestConfig | null = null): Promise<HttpResult<any>> {
    return request(this.baseUrlResolver()).post(url, data, {
      responseType: "blob",
      showNotificationOnSuccess: false,
      showNotificationOnError: true,
      ...config,
    });
  }

  /**
   * Sends a PUT request to the backend
   * @param {string} url - Api url
   * @param {object} data - data to be send
   * @param {boolean} showNotificationOnSuccess - Value indicating whether to show a notification on success
   * @returns {Promise} Response data
   */
  put(url: string, data: any, showNotificationOnSuccess = false): Promise<HttpResult<any>> {
    return request(this.baseUrlResolver()).put(url, data, {
      showNotificationOnSuccess: showNotificationOnSuccess,
      showNotificationOnError: true,
    } as HttpRequestConfig);
  }

  /**
   * Sends a DELETE request to the backend
   * @param {string} url - Api url
   * @param {boolean} showNotificationOnSuccess - Value indicating whether to show a notification on success
   * @param {object} data - optional data
   * @returns {Promise} Response data
   */
  delete(url: string, showNotificationOnSuccess = false, data: any | null = null): Promise<HttpResult<any>> {
    return request(this.baseUrlResolver()).delete(url, {
      showNotificationOnSuccess: showNotificationOnSuccess,
      showNotificationOnError: true,
      data: data,
    } as HttpRequestConfig);
  }
}
